import React, { useState } from 'react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import { ApiConnectionCredentials, EMPTY_API_CREDENTIALS } from './apiCredentials.types.ts';
import { Send, Settings, Mail, PenTool, CheckCircle, Copy, Check, Info, FileCode, Activity, KeyRound, Pencil } from 'lucide-react';

interface ApiEndpoint {
  id: string;
  name: string;
  method: 'GET' | 'POST';
  path: string;
  description: string;
  defaultPayload?: string;
  mockResponse: string;
  statusChange?: 'sent' | 'delivered' | 'completed';
}

const DOCUSIGN_ENDPOINTS: ApiEndpoint[] = [
  {
    id: 'env_post',
    name: 'Crear Sobre (Enviar Contrato)',
    method: 'POST',
    path: '/restapi/v2.1/accounts/12884011/envelopes',
    description: 'Envía un documento PDF a uno o más destinatarios para su firma digital.',
    statusChange: 'sent',
    defaultPayload: JSON.stringify({
      emailSubject: "Por favor firma el Contrato de Servicios - OneHub",
      documents: [
        {
          documentBase64: "JVBERi0xLjQKJcFS...",
          name: "ContratoServicios.pdf",
          fileExtension: "pdf",
          documentId: "1"
        }
      ],
      recipients: {
        signers: [
          {
            email: "yeison.ortiz@onehub.com",
            name: "Yeison Ortiz",
            recipientId: "1",
            routingOrder: "1"
          }
        ]
      },
      status: "sent"
    }, null, 2),
    mockResponse: JSON.stringify({
      envelopeId: "8f5a40db-98cb-4e92-bc2f-1a980753a811",
      uri: "/envelopes/8f5a40db-98cb-4e92-bc2f-1a980753a811",
      statusDateTime: new Date().toISOString(),
      status: "sent"
    }, null, 2)
  },
  {
    id: 'env_get_status',
    name: 'Consultar Estado de Sobre',
    method: 'GET',
    path: '/restapi/v2.1/accounts/12884011/envelopes/8f5a40db-98cb-4e92-bc2f-1a980753a811',
    description: 'Obtiene el estado detallado de firmas de un sobre específico por su ID.',
    statusChange: 'delivered',
    mockResponse: JSON.stringify({
      envelopeId: "8f5a40db-98cb-4e92-bc2f-1a980753a811",
      status: "delivered",
      emailSubject: "Por favor firma el Contrato de Servicios - OneHub",
      sentDateTime: new Date(Date.now() - 3600000).toISOString(),
      deliveredDateTime: new Date().toISOString(),
      recipients: {
        signers: [
          {
            name: "Yeison Ortiz",
            email: "yeison.ortiz@onehub.com",
            recipientId: "1",
            status: "delivered",
            deliveredDateTime: new Date().toISOString()
          }
        ]
      }
    }, null, 2)
  },
  {
    id: 'env_completed',
    name: 'Consultar Firma Completada',
    method: 'GET',
    path: '/restapi/v2.1/accounts/12884011/envelopes/8f5a40db-98cb-4e92-bc2f-1a980753a811',
    description: 'Consulta final que retorna el estado del sobre marcado como Completado (firmado).',
    statusChange: 'completed',
    mockResponse: JSON.stringify({
      envelopeId: "8f5a40db-98cb-4e92-bc2f-1a980753a811",
      status: "completed",
      emailSubject: "Por favor firma el Contrato de Servicios - OneHub",
      sentDateTime: new Date(Date.now() - 7200000).toISOString(),
      deliveredDateTime: new Date(Date.now() - 3600000).toISOString(),
      completedDateTime: new Date().toISOString(),
      recipients: {
        signers: [
          {
            name: "Yeison Ortiz",
            email: "yeison.ortiz@onehub.com",
            recipientId: "1",
            status: "completed",
            signedDateTime: new Date().toISOString()
          }
        ]
      }
    }, null, 2)
  }
];

type EnvelopeState = 'created' | 'sent' | 'delivered' | 'completed';

interface DocusignApisProps {
  setPage: (page: string) => void;
  embedded?: boolean;
  credentials?: ApiConnectionCredentials;
}

export default function DocusignApis({ setPage, embedded = false, credentials = EMPTY_API_CREDENTIALS }: DocusignApisProps) {
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint>(DOCUSIGN_ENDPOINTS[0]);
  const [payload, setPayload] = useState<string>(selectedEndpoint.defaultPayload || '');
  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'params' | 'headers'>('params');

  // Estado del ciclo de vida del sobre (stepper)
  const [envelopeState, setEnvelopeState] = useState<EnvelopeState>('created');

  const handleEndpointSelect = (ep: ApiEndpoint) => {
    setSelectedEndpoint(ep);
    setPayload(ep.defaultPayload || '');
    setResponse('');
  };

  const handleCopyResponse = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendRequest = () => {
    setLoading(true);
    setResponse('');
    setTimeout(() => {
      setResponse(selectedEndpoint.mockResponse);
      if (selectedEndpoint.statusChange) {
        setEnvelopeState(selectedEndpoint.statusChange);
      }
      setLoading(false);
    }, 1000);
  };

  const handleResetStepper = () => {
    setEnvelopeState('created');
    setResponse('');
  };

  // Definición de pasos para el stepper
  const steps = [
    { key: 'created', label: 'Borrador', desc: 'Sobre listo para envío', icon: Settings },
    { key: 'sent', label: 'Enviado', desc: 'Correo enviado al firmante', icon: Mail },
    { key: 'delivered', label: 'Entregado', desc: 'Documento abierto/leído', icon: PenTool },
    { key: 'completed', label: 'Completado', desc: 'Documento firmado con éxito', icon: CheckCircle },
  ];

  const getStepStatusClass = (stepKey: EnvelopeState, index: number) => {
    const statesOrder: EnvelopeState[] = ['created', 'sent', 'delivered', 'completed'];
    const currentIndex = statesOrder.indexOf(envelopeState);
    
    if (stepKey === envelopeState) {
      return 'bg-purple-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.5)] border-purple-500';
    }
    if (index < currentIndex) {
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40';
    }
    return 'bg-white/5 text-gray-500 border-white/10';
  };

  const getLineStatusClass = (index: number) => {
    const statesOrder: EnvelopeState[] = ['created', 'sent', 'delivered', 'completed'];
    const currentIndex = statesOrder.indexOf(envelopeState);
    if (index < currentIndex) {
      return 'bg-emerald-500';
    }
    return 'bg-white/10';
  };

  return (
    <div className={embedded ? 'text-white' : 'min-h-screen p-4 md:p-8 text-white bg-transparent relative overflow-hidden'}>
      {!embedded && (
        <>
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-pink-900/10 rounded-full blur-[100px] pointer-events-none z-0" />
        </>
      )}

      <div className={embedded ? 'flex flex-col h-full' : 'relative z-10 max-w-7xl mx-auto flex flex-col h-full'}>
        {!embedded && <Breadcrumbs currentPage="docusign-apis" setPage={setPage} />}

        <header className="mb-8 mt-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="p-2 bg-purple-500/20 rounded-xl text-purple-400">
              <PenTool className="w-8 h-8" />
            </span>
            Docusign eSignature API Explorer
          </h1>
          <p className="text-gray-400 text-sm font-light mt-1 max-w-2xl">
            Simula el flujo de firma electrónica de Docusign REST API. Interactúa con las llamadas para crear sobres, seguir el ciclo de vida y verificar la firma de contratos.
          </p>
        </header>

        <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-xs">
          <span className={`flex items-center gap-1.5 font-bold uppercase tracking-wider ${credentials.clientId ? 'text-emerald-400' : 'text-amber-400'}`}>
            <KeyRound className="w-3.5 h-3.5" /> {credentials.clientId ? 'Credenciales activas' : 'Sin credenciales configuradas'}
          </span>
          {credentials.clientId && (
            <>
              <span className="text-gray-500">Client ID: <span className="text-white font-mono">{credentials.clientId}</span></span>
              <span className="text-gray-500">Data Center: <span className="text-white font-mono">{credentials.dataCenter || '-'}</span></span>
            </>
          )}
          <button
            onClick={() => setPage('pruebas-apis')}
            className="ml-auto flex items-center gap-1.5 text-purple-400 hover:text-purple-300 font-semibold transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> Editar credenciales
          </button>
        </div>

        {/* STEPPER VISUAL INTERACTIVO */}
        <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-400 animate-pulse" /> Ciclo de Vida del Contrato (Sobre)
            </h3>
            <button
              onClick={handleResetStepper}
              className="text-xs font-bold text-purple-400 hover:text-purple-300 transition-colors"
            >
              Reiniciar Ciclo
            </button>
          </div>

          <div className="relative flex flex-col md:flex-row justify-between items-center md:items-start gap-8 md:gap-4 px-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div key={step.key} className="flex-1 flex flex-col items-center text-center relative w-full">
                  {/* Línea conectora */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-7 left-[55%] right-[-45%] h-0.5 z-0">
                      <div className={`h-full w-full rounded transition-all duration-500 ${getLineStatusClass(index)}`} />
                    </div>
                  )}

                  {/* Círculo del icono */}
                  <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center relative z-10 transition-all duration-500 ${getStepStatusClass(step.key as EnvelopeState, index)}`}>
                    <StepIcon className="w-6 h-6" />
                  </div>

                  {/* Detalle */}
                  <h4 className="mt-4 font-bold text-sm text-white">{step.label}</h4>
                  <p className="text-gray-500 text-xs mt-1 font-light max-w-[150px]">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* SIDEBAR CON SERVICIOS */}
          <aside className="lg:col-span-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 flex flex-col gap-2">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 px-3">
              Operaciones eSignature
            </h3>
            <div className="space-y-1.5 overflow-y-auto max-h-[500px]">
              {DOCUSIGN_ENDPOINTS.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => handleEndpointSelect(ep)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex flex-col gap-1 ${
                    selectedEndpoint.id === ep.id
                      ? 'bg-purple-600/90 text-white shadow-lg'
                      : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                        ep.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {ep.method}
                    </span>
                    <span className="font-semibold text-xs truncate">{ep.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* REQUEST BUILDER */}
          <section className="lg:col-span-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
            <div className="space-y-6">
              {/* Info del Endpoint */}
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-400" /> Configurar Petición
                </h3>
                <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                  {selectedEndpoint.description}
                </p>
              </div>

              {/* URL/Path Bar */}
              <div className="bg-black/30 border border-white/10 rounded-xl p-3.5 flex items-center gap-2.5 font-mono text-xs overflow-hidden">
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                    selectedEndpoint.method === 'GET'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-blue-500/20 text-blue-400'
                  }`}
                >
                  {selectedEndpoint.method}
                </span>
                <span className="text-gray-300 truncate w-full" title={selectedEndpoint.path}>
                  {selectedEndpoint.path}
                </span>
              </div>

              {/* Tabs para Payload / Headers */}
              <div>
                <div className="flex border-b border-white/10 mb-4 text-xs font-semibold">
                  <button
                    onClick={() => setActiveTab('params')}
                    className={`pb-2.5 px-4 relative ${
                      activeTab === 'params' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    Payload JSON
                  </button>
                  <button
                    onClick={() => setActiveTab('headers')}
                    className={`pb-2.5 px-4 relative ${
                      activeTab === 'headers' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    Cabeceras
                  </button>
                </div>

                {activeTab === 'params' ? (
                  <div>
                    {selectedEndpoint.method === 'POST' ? (
                      <textarea
                        className="w-full h-72 font-mono text-xs bg-black/40 border border-white/10 rounded-xl p-4 text-purple-200 outline-none focus:border-purple-500 resize-none"
                        value={payload}
                        onChange={(e) => setPayload(e.target.value)}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center h-72 border border-white/5 rounded-xl bg-black/10 text-gray-500">
                        <Info className="w-8 h-8 text-gray-600 mb-2" />
                        <p className="text-xs">Los métodos GET no requieren cuerpo de envío.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 h-72 overflow-y-auto pr-2">
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">Content-Type</span>
                      <span className="text-purple-300">application/json</span>
                    </div>
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">Accept</span>
                      <span className="text-purple-300">application/json</span>
                    </div>
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">X-DocuSign-SDK</span>
                      <span className="text-purple-300">Node.js eSign v5.1</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ejecutar */}
            <button
              onClick={handleSendRequest}
              disabled={loading}
              className="mt-6 w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all transform active:scale-[0.98]"
            >
              <Send className="w-4 h-4 fill-white" />
              {loading ? 'Procesando...' : 'Enviar Petición'}
            </button>
          </section>

          {/* RESPONSE VIEWER */}
          <section className="lg:col-span-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <FileCode className="w-5 h-5 text-pink-400" /> Consola de Salida
                </h3>
                {response && (
                  <button
                    onClick={handleCopyResponse}
                    className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                    title="Copiar respuesta"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {/* Consola */}
              <div className="flex-1 bg-black/50 border border-white/10 rounded-2xl p-4 font-mono text-[11px] leading-normal overflow-auto min-h-[300px] max-h-[360px] custom-scrollbar text-purple-300">
                {loading ? (
                  <span className="text-gray-500 animate-pulse block">Conectando con Docusign eSignature API...</span>
                ) : response ? (
                  <pre className="whitespace-pre-wrap">{response}</pre>
                ) : (
                  <span className="text-gray-600 italic block">Envía una petición para visualizar la respuesta del servidor...</span>
                )}
              </div>
            </div>

            {/* Status Info */}
            {response && !loading && (
              <div className="mt-6 flex justify-between items-center text-xs border-t border-white/10 pt-4 text-gray-500">
                <span>Status: <strong className="text-emerald-400 font-bold">200 OK</strong></span>
                <span>Time: <strong className="text-white">182 ms</strong></span>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
